import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { AlertTriangle, Search, User, Loader2, RefreshCw, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";

const API = window.location.origin + "/api";

/**
 * Widget della pagina Feedback che mostra i feedback ricevuti ma che il
 * backend non è riuscito ad associare automaticamente a un contatto GHL
 * (in genere per nomi scritti male dal paziente). Consente:
 *   - vedere il feedback originale (ratings + commento)
 *   - cercare il contatto GHL corretto e associarlo → posta la nota su GHL
 *   - scartare la task (feedback di test, anonimo, ecc.)
 *   - lanciare il backfill sui feedback storici (solo admin)
 */
export default function UnmatchedFeedbackPanel({ isAdmin }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [resolveDialog, setResolveDialog] = useState({ open: false, task: null });
  const [backfillJob, setBackfillJob] = useState(null); // {status, processed, total, stats}
  const [pollingJobId, setPollingJobId] = useState(null);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/feedback/unmatched`, { withCredentials: true });
      setTasks(res.data.tasks || []);
    } catch (err) {
      console.error("fetch unmatched:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Al mount: se un job di backfill è già in corso (avviato da un altro
  // admin o rimasto appeso), lo mostriamo. Il polling parte automaticamente.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/feedback/backfill-status`, { withCredentials: true });
        if (!cancelled && res.data.job) {
          setBackfillJob(res.data.job);
          if (res.data.job.status === "running") setPollingJobId(res.data.job.job_id);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Polling del progresso ogni 2s finché il job è running
  useEffect(() => {
    if (!pollingJobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/feedback/backfill-status`, { withCredentials: true });
        if (cancelled) return;
        const job = res.data.job;
        setBackfillJob(job);
        if (!job || job.status !== "running") {
          setPollingJobId(null);
          if (job?.status === "done") {
            const s = job.stats || {};
            toast.success(
              `Backfill completato: ${s.synced || 0} sincronizzati · ${s.unmatched || 0} da associare · ${s.errors || 0} errori`
            );
            fetchTasks();
          } else if (job?.status === "error") {
            toast.error(`Backfill fallito: ${job.error || "errore sconosciuto"}`);
          }
        }
      } catch (err) {
        console.error("poll backfill:", err);
      }
    };
    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [pollingJobId, fetchTasks]);

  const handleBackfill = async () => {
    if (!window.confirm(
      "Vuoi ora provare a sincronizzare TUTTI i feedback non ancora associati?\n\n" +
      "Il sistema cercherà il contatto GHL corrispondente per ognuno e posterà la nota. " +
      "L'operazione gira in background — puoi chiudere questa pagina, tornerà a mostrare il progresso al rientro."
    )) return;
    try {
      const res = await axios.post(
        `${API}/feedback/backfill-ghl-notes?limit=500&only_unsynced=true`,
        {},
        { withCredentials: true }
      );
      toast.success("Sincronizzazione avviata in background");
      setBackfillJob({ status: "running", processed: 0, total: 0, stats: {} });
      setPollingJobId(res.data.job_id);
    } catch (err) {
      console.error("backfill:", err);
      if (err.response?.status === 409) {
        toast.error("Un backfill è già in corso");
      } else {
        toast.error(err.response?.data?.detail || "Errore durante il backfill");
      }
    }
  };

  const handleDismiss = async (task) => {
    if (!window.confirm(`Cancellare l'evidenza di "${task.patient_name || 'Anonimo'}"? L'operazione è definitiva e non verrà salvata su GHL.`)) return;
    try {
      await axios.delete(`${API}/feedback/unmatched/${task.task_id}`, { withCredentials: true });
      setTasks((prev) => prev.filter((t) => t.task_id !== task.task_id));
      toast.success("Evidenza cancellata");
    } catch (err) {
      console.error("dismiss:", err);
      toast.error("Errore durante la cancellazione");
    }
  };

  if (loading) {
    return (
      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm text-amber-700 flex items-center gap-2" data-testid="unmatched-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Verifica sincronizzazione feedback → GHL…
      </div>
    );
  }

  const count = tasks.length;
  const totalTitle = count === 0
    ? "Tutti i feedback sono associati ai contatti GHL"
    : `${count} feedback ${count === 1 ? "non associato" : "non associati"} a un contatto GHL`;

  const jobRunning = backfillJob?.status === "running";
  const jobPct = jobRunning && backfillJob?.total > 0
    ? Math.round((backfillJob.processed / backfillJob.total) * 100)
    : 0;

  return (
    <div
      className={`rounded-xl border p-4 ${count > 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}
      data-testid="unmatched-feedback-panel"
    >
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          className="flex items-center gap-3 text-left flex-1 min-w-0"
          onClick={() => setExpanded((e) => !e)}
          data-testid="unmatched-toggle"
          disabled={count === 0}
        >
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${count > 0 ? "bg-amber-500" : "bg-emerald-500"}`}>
            <AlertTriangle className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0">
            <p className={`text-sm font-semibold ${count > 0 ? "text-amber-800" : "text-emerald-800"}`}>{totalTitle}</p>
            <p className={`text-xs ${count > 0 ? "text-amber-700" : "text-emerald-700"}`}>
              I feedback vengono automaticamente salvati come nota nel campo <b>Notes</b> del contatto GoHighLevel.
            </p>
          </div>
          {count > 0 && (expanded ? <ChevronUp className="w-4 h-4 text-amber-600 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-amber-600 flex-shrink-0" />)}
        </button>

        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleBackfill}
            disabled={jobRunning}
            data-testid="unmatched-backfill-btn"
            className="flex-shrink-0"
          >
            {jobRunning ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Sync… {jobPct}%</>
            ) : (
              <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Sincronizza storici</>
            )}
          </Button>
        )}
      </div>

      {jobRunning && (
        <div className="mt-3" data-testid="backfill-progress">
          <div className="h-2 bg-white/60 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-500 transition-all duration-500"
              style={{ width: `${jobPct}%` }}
            />
          </div>
          <p className="text-xs text-amber-700 mt-1">
            Sincronizzati {backfillJob.processed || 0}/{backfillJob.total || 0}
            {backfillJob.stats && (
              <> · ✅ {backfillJob.stats.synced || 0} · ⚠️ {backfillJob.stats.unmatched || 0} da associare · ❌ {backfillJob.stats.errors || 0}</>
            )}
          </p>
        </div>
      )}

      {expanded && count > 0 && (
        <div className="mt-4 space-y-2 max-h-[420px] overflow-y-auto" data-testid="unmatched-list">
          {tasks.map((t) => (
            <TaskCard
              key={t.task_id}
              task={t}
              onResolve={() => setResolveDialog({ open: true, task: t })}
              onDismiss={() => handleDismiss(t)}
            />
          ))}
        </div>
      )}

      <ResolveDialog
        open={resolveDialog.open}
        task={resolveDialog.task}
        onClose={() => setResolveDialog({ open: false, task: null })}
        onSuccess={(taskId) => {
          setTasks((prev) => prev.filter((t) => t.task_id !== taskId));
          setResolveDialog({ open: false, task: null });
        }}
      />
    </div>
  );
}


function TaskCard({ task, onResolve, onDismiss }) {
  const fb = task.feedback || {};
  const submittedAt = new Date(task.submitted_at || fb.submitted_at || Date.now());
  const submittedFmt = submittedAt.toLocaleString("it-IT", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  const avg = (() => {
    const ratings = (fb.answers || []).map((a) => a.rating).filter((r) => typeof r === "number");
    if (!ratings.length) return null;
    return (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1);
  })();

  // Preferisci i campi sul task (salvati al momento della creazione), poi
  // fallback sul feedback originale per retro-compatibilità con task
  // create prima dell'introduzione dei campi phone/email.
  const email = task.patient_email || fb.email || "";
  const phone = task.patient_phone || fb.phone || "";

  return (
    <div className="bg-white border border-amber-100 rounded-lg p-3 flex items-center gap-3" data-testid={`unmatched-item-${task.task_id}`}>
      <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
        <User className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 truncate">
          {task.patient_name || <span className="italic text-slate-400">Anonimo</span>}
        </p>
        <p className="text-xs text-slate-500">
          {submittedFmt}
          {avg && <> · <span className="font-semibold text-slate-600">⭐ {avg}/5</span></>}
          {fb.patient_type && <> · <span className="capitalize">{fb.patient_type}</span></>}
        </p>
        {(email || phone) && (
          <p className="text-xs text-blue-600 mt-0.5 font-medium truncate" data-testid={`unmatched-contact-${task.task_id}`}>
            {email && <span>✉ {email}</span>}
            {email && phone && <span className="text-slate-400"> · </span>}
            {phone && <span>☎ {phone}</span>}
          </p>
        )}
        {fb.comment && (
          <p className="text-xs text-slate-500 truncate mt-0.5 italic">&ldquo;{fb.comment}&rdquo;</p>
        )}
      </div>
      <Button
        variant="default"
        size="sm"
        onClick={onResolve}
        data-testid={`unmatched-resolve-${task.task_id}`}
        className="bg-amber-500 hover:bg-amber-600 flex-shrink-0"
      >
        Associa
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onDismiss}
        data-testid={`unmatched-dismiss-${task.task_id}`}
        className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300 flex-shrink-0"
        title="Cancella definitivamente questa evidenza (non verrà salvata su GHL)"
      >
        <Trash2 className="w-4 h-4 mr-1.5" />
        Cancella
      </Button>
    </div>
  );
}


function ResolveDialog({ open, task, onClose, onSuccess }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery(task?.patient_name || "");
      setResults([]);
      setSelected(null);
    }
  }, [open, task]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const q = (query || "").trim();
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await axios.get(
          `${API}/feedback/ghl-search?q=${encodeURIComponent(q)}`,
          { withCredentials: true }
        );
        setResults(res.data.contacts || []);
      } catch (err) {
        console.error("ghl-search:", err);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query, open]);

  const handleConfirm = async () => {
    if (!selected || !task) return;
    try {
      setSaving(true);
      await axios.post(
        `${API}/feedback/unmatched/${task.task_id}/resolve`,
        { ghl_contact_id: selected.ghl_id },
        { withCredentials: true }
      );
      toast.success(`Nota salvata su ${selected.contactName}`);
      onSuccess(task.task_id);
    } catch (err) {
      console.error("resolve:", err);
      toast.error(err.response?.data?.detail || "Errore durante l'associazione");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="unmatched-resolve-dialog">
        <DialogHeader>
          <DialogTitle>Associa feedback a contatto GHL</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-slate-50 rounded-lg p-3 text-sm">
            <span className="text-slate-500">Feedback di:</span>{" "}
            <span className="font-semibold text-slate-800">{task?.patient_name || "Anonimo"}</span>
          </div>

          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca contatto GHL per nome, email, telefono…"
              data-testid="unmatched-search-input"
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:border-blue-400"
              autoFocus
            />
            {searching && (
              <Loader2 className="w-4 h-4 text-slate-400 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />
            )}
          </div>

          <div className="max-h-64 overflow-y-auto space-y-1 border border-slate-100 rounded-lg" data-testid="unmatched-search-results">
            {results.length === 0 && query.length >= 2 && !searching && (
              <p className="text-xs text-slate-400 italic p-3">Nessun contatto trovato</p>
            )}
            {results.map((c) => (
              <button
                key={c.ghl_id}
                type="button"
                onClick={() => setSelected(c)}
                data-testid={`unmatched-result-${c.ghl_id}`}
                className={`w-full text-left p-2.5 hover:bg-blue-50 flex items-center gap-3 border-b border-slate-100 last:border-b-0 ${
                  selected?.ghl_id === c.ghl_id ? "bg-blue-100" : ""
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center flex-shrink-0 capitalize text-sm font-semibold">
                  {(c.firstName || c.contactName || "?").charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 capitalize truncate">{c.contactName}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {c.email || <span className="italic text-slate-300">(no email)</span>}
                    {c.phone && <> · {c.phone}</>}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="unmatched-cancel-btn">Annulla</Button>
          <Button
            onClick={handleConfirm}
            disabled={!selected || saving}
            data-testid="unmatched-confirm-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {saving ? (<><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Salvo…</>) : "Associa e salva su GHL"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
